import React, { useEffect, useState } from 'react';
import { SlideOver } from '../../admin/shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { DOMAIN_META, getSubTestDefinition, DOMAIN_MODULE_MAP, isBegrijpendLezenFamily, isTaalverzorgingTaaFamily } from '../../../assessment/utils/assessmentDomainConfig';
import type { AssessmentDomainId } from '../../../assessment/types';
import { error as logError } from '../../../utils/logger';
import { dataLayer } from '../../../data/dataLayer';
import { Clock, HelpCircle, Users, Calendar, CheckCircle2, Search, Layers, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { FAMILIES_BY_MODULE } from '../../../content/taxonomy/taxonomyRegistry';
import type { FamilyDefinition } from '../../../content/taxonomy/types';
import { DjayAvatar } from '../../../mascot';
interface CreateAssessmentPanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3 | 4;
function getFamiliesForDomain(domainId: AssessmentDomainId): FamilyDefinition[] {
  const modules = DOMAIN_MODULE_MAP[domainId];
  let families = modules.flatMap(mod => FAMILIES_BY_MODULE.get(mod) || []);
  if (domainId === 'taalverzorging') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isTaalverzorgingTaaFamily(f.familyId));
  } else if (domainId === 'begrijpend-lezen') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isBegrijpendLezenFamily(f.familyId));
  }
  return families;
}
export function CreateAssessmentPanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreateAssessmentPanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [selectedDomain, setSelectedDomain] = useState<AssessmentDomainId | null>(null);
  // Step 2 state
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset state when opened
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedDomain(null);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
    }
  }, [open]);
  // Initialize with EMPTY array when domain changes (additive selection)
  useEffect(() => {
    if (selectedDomain) {
      setSelectedFamilyIds([]);
      setSearchQuery('');
      setExpandedSkills({});
    }
  }, [selectedDomain]);
  const handleNext = () => {
    if (step < 4) setStep(s => s + 1 as Step);
  };
  const handleBack = () => {
    if (step > 1) setStep(s => s - 1 as Step);
  };
  const handleConfirm = async () => {
    if (!selectedDomain) return;
    setIsSubmitting(true);
    const domainDef = getSubTestDefinition(selectedDomain, selectedGroup);
    const questionCount = domainDef?.questionCount || 0;
    const estimatedMinutes = domainDef?.estimatedMinutes || 0;
    const assignmentId = `assignment_${Date.now()}`;
    const allFamilies = getFamiliesForDomain(selectedDomain);
    const selectedFams = allFamilies.filter(f => selectedFamilyIds.includes(f.familyId));
    const uniqueSkills = Array.from(new Set(selectedFams.map(f => f.skill.replace(/_/g, ' '))));
    const skillSummary = uniqueSkills.join(', ');
    const wholeClass = selectionMode === 'whole';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      domainId: selectedDomain,
      domainLabel: DOMAIN_META[selectedDomain].label,
      group: selectedGroup,
      questionCount,
      estimatedMinutes,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      selectedFamilyIds: selectedFamilyIds,
      type: 'targeted' as const,
      selectedSkillSummary: skillSummary,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active'
    };
    try {
      await dataLayer.saveTeacherAssignment(assignmentId, assignment);
      onSuccess();
      onClose();
    } catch (error) {
      logError('Failed to save assignment', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const renderStep1 = () => <div className="space-y-6" data-id="element-5320">
      <div className="bg-orange-50 rounded-xl p-3 flex items-center gap-3 border border-orange-100" data-id="element-5321">
        <div className="flex-shrink-0" data-id="element-5322">
          <DjayAvatar size="sm" emotion="happy" data-id="element-5323" />
        </div>
        <div data-id="element-5324">
          <h3 className="font-bold text-orange-900 text-sm mb-0.5" data-id="element-5325">
            Kies een vakgebied
          </h3>
          <p className="text-xs text-orange-800" data-id="element-5326">
            Welk vak wil je toetsen? De toets wordt automatisch afgestemd op het
            niveau van groep {selectedGroup}.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-id="element-5327">
        {domains.map(domainId => {
        const meta = DOMAIN_META[domainId];
        const def = getSubTestDefinition(domainId, selectedGroup);
        const isAvailable = def !== null && def.available;
        const isSelected = selectedDomain === domainId;
        return <div key={domainId} onClick={() => isAvailable && setSelectedDomain(domainId)} className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${!isAvailable ? 'opacity-50 border-slate-100 bg-slate-50 cursor-not-allowed' : isSelected ? 'border-orange-500 bg-orange-50/50 shadow-sm' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-5328">
              <div className="flex items-start gap-4" data-id="element-5329">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-5330">
                  <meta.icon className="w-6 h-6" data-id="element-5331" />
                </div>
                <div className="flex-1" data-id="element-5332">
                  <div className="flex items-center justify-between" data-id="element-5333">
                    <h3 className={`font-bold text-lg ${isSelected ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-5334">
                      {meta.label}
                    </h3>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-orange-500" data-id="element-5335" />}
                  </div>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-5336">
                    {meta.description}
                  </p>

                  {isAvailable && def && <div className="flex items-center gap-4 mt-3 text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5337">
                      <div className="flex items-center gap-1.5" data-id="element-5338">
                        <HelpCircle className="w-3.5 h-3.5" data-id="element-5339" />
                        {def.questionCount} vragen
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-5340">
                        <Clock className="w-3.5 h-3.5" data-id="element-5341" />~{def.estimatedMinutes}{' '}
                        min
                      </div>
                    </div>}
                  {!isAvailable && <div className="mt-2 text-xs font-medium text-rose-500 bg-rose-50 px-2 py-1 rounded inline-block" data-id="element-5342">
                      Niet beschikbaar voor groep {selectedGroup}
                    </div>}
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
  const renderFamilyPicker = () => {
    const allFamilies = selectedDomain ? getFamiliesForDomain(selectedDomain) : [];
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    // Group by skill only (module already chosen in step 1)
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const selectedCount = selectedFamilyIds.length;
    return <div className="space-y-4" data-id="element-5343">
        <div className="bg-orange-50 border border-orange-100 rounded-lg p-3" data-id="element-5344">
          <div className="text-sm font-bold text-orange-900" data-id="element-5345">
            {selectedCount} {selectedCount === 1 ? 'onderdeel' : 'onderdelen'}{' '}
            geselecteerd
          </div>
          <p className="text-xs text-orange-700 mt-1" data-id="element-5346">
            Kies de onderdelen die je wilt toetsen
          </p>
        </div>

        <div className="relative" data-id="element-5347">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-5348" />
          <Input placeholder="Zoek op onderdeel..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white" data-id="element-5349" />
        </div>

        <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2" data-id="element-5350">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-5351">
                <div className="bg-slate-50 flex items-center justify-between p-3" data-id="element-5352">
                  <div className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => toggleExpand(skill)} data-id="element-5353">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" data-id="element-5354" /> : <ChevronRight className="w-4 h-4 text-slate-500" data-id="element-5355" />}
                    <h4 className="text-sm font-bold text-slate-700" data-id="element-5356">
                      {skill.replace(/_/g, ' ')}
                    </h4>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-5357">
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-1" onClick={() => toggleSkillGroup(families)} data-id="element-5358">
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${isAllSelected ? 'bg-orange-500 border-orange-500 text-white' : isPartiallySelected ? 'bg-orange-100 border-orange-500 text-orange-500' : 'border-slate-300 bg-white'}`} data-id="element-5359">
                      {isAllSelected && <Check className="w-3.5 h-3.5" data-id="element-5360" />}
                      {isPartiallySelected && <div className="w-2.5 h-0.5 bg-current rounded-full" data-id="element-5361" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="divide-y divide-slate-100" data-id="element-5362">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <div key={f.familyId} onClick={() => toggleFamily(f.familyId)} className="flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-slate-50" data-id="element-5363">
                          <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-5364">
                            {isSelected && <Check className="w-3.5 h-3.5" data-id="element-5365" />}
                          </div>
                          <div data-id="element-5366">
                            <div className={`text-sm font-medium ${isSelected ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-5367">
                              {f.description}
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-5368">
                              Niveau {f.levelCode}
                            </div>
                          </div>
                        </div>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-500 text-sm" data-id="element-5369">
              Geen onderdelen gevonden voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  const renderStep2 = () => <div className="space-y-6" data-id="element-5370">
      <div className="bg-orange-50 rounded-xl p-3 flex items-center gap-3 border border-orange-100" data-id="element-5371">
        <div className="flex-shrink-0" data-id="element-5372">
          <DjayAvatar size="sm" emotion="thinking" data-id="element-5373" />
        </div>
        <div data-id="element-5374">
          <h3 className="font-bold text-orange-900 text-sm mb-0.5" data-id="element-5375">
            Welke onderdelen?
          </h3>
          <p className="text-xs text-orange-800" data-id="element-5376">
            Kies de specifieke onderdelen die je wilt toetsen.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-id="element-5377">
        <Card className="p-4 border-slate-200 bg-slate-50/50" data-id="element-5378">
          {renderFamilyPicker()}
        </Card>
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-6" data-id="element-5379">
      <div className="bg-orange-50 rounded-xl p-3 flex items-center gap-3 border border-orange-100" data-id="element-5380">
        <div className="flex-shrink-0" data-id="element-5381">
          <DjayAvatar size="sm" emotion="thinking" data-id="element-5382" />
        </div>
        <div data-id="element-5383">
          <h3 className="font-bold text-orange-900 text-sm mb-0.5" data-id="element-5384">
            Voor wie?
          </h3>
          <p className="text-xs text-orange-800" data-id="element-5385">
            Kies voor welke leerlingen je deze toets wilt klaarzetten.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-id="element-5386">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5387">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${selectionMode === 'whole' ? 'border-orange-500 bg-orange-50/50 shadow-sm' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-5388">
            <div className="flex items-center justify-between mb-2" data-id="element-5389">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectionMode === 'whole' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-5390">
                <Users className="w-4 h-4" data-id="element-5391" />
              </div>
              <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${selectionMode === 'whole' ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-5392">
                {selectionMode === 'whole' && <Check className="w-3.5 h-3.5" data-id="element-5393" />}
              </div>
            </div>
            <h4 className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-5394">
              Hele klas
            </h4>
            <p className="text-xs text-slate-500 mt-1" data-id="element-5395">
              Alle {students.length} leerlingen
            </p>
          </div>

          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${selectionMode === 'specific' ? 'border-orange-500 bg-orange-50/50 shadow-sm' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-5396">
            <div className="flex items-center justify-between mb-2" data-id="element-5397">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectionMode === 'specific' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-5398">
                <Search className="w-4 h-4" data-id="element-5399" />
              </div>
              <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${selectionMode === 'specific' ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-5400">
                {selectionMode === 'specific' && <Check className="w-3.5 h-3.5" data-id="element-5401" />}
              </div>
            </div>
            <h4 className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-5402">
              Specifieke leerlingen
            </h4>
            <p className="text-xs text-slate-500 mt-1" data-id="element-5403">
              Kies individuele leerlingen
            </p>
          </div>
        </div>

        <Card className="p-4 border-slate-200 mt-4" data-id="element-5404">
          {selectionMode === 'whole' && <div className="text-xs font-medium text-amber-600 bg-amber-50 px-3 py-2 rounded-lg flex items-start gap-2" data-id="element-5405">
              <HelpCircle className="w-4 h-4 flex-shrink-0" data-id="element-5406" />
              <span data-id="element-5407">
                Let op: de toets wordt voor{' '}
                <strong data-id="element-5408">alle {students.length} leerlingen</strong> in de klas
                klaargezet.
              </span>
            </div>}

          {selectionMode === 'specific' && <div data-id="element-5409">
              <div className="text-xs font-bold text-slate-700 mb-3" data-id="element-5410">
                Selecteer leerlingen ({selectedStudentIds.length}/
                {students.length})
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2" data-id="element-5411">
                {students.map(student => {
              const isSelected = selectedStudentIds.includes(student.id);
              return <div key={student.id} onClick={() => toggleStudent(student.id)} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-orange-200 bg-orange-50/50' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-5412">
                      <div className="flex items-center gap-3" data-id="element-5413">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600" data-id="element-5414">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-900" data-id="element-5415">
                          {student.name}
                        </span>
                      </div>
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-5416">
                        {isSelected && <Check className="w-3.5 h-3.5" data-id="element-5417" />}
                      </div>
                    </div>;
            })}
              </div>
            </div>}

          {selectionMode === null && <div className="text-center py-6 text-slate-500 text-sm" data-id="element-5418">
              Kies eerst voor wie je de toets wilt klaarzetten.
            </div>}
        </Card>
      </div>
    </div>;
  const renderConfirmStep = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    const allFamilies = selectedDomain ? getFamiliesForDomain(selectedDomain) : [];
    const selectedFams = allFamilies.filter(f => selectedFamilyIds.includes(f.familyId));
    const uniqueSkills = Array.from(new Set(selectedFams.map(f => f.skill.replace(/_/g, ' '))));
    return <div className="space-y-6" data-id="element-5419">
        <div className="bg-orange-50 rounded-xl p-3 flex items-center gap-3 border border-orange-100" data-id="element-5420">
          <div className="flex-shrink-0" data-id="element-5421">
            <DjayAvatar size="sm" emotion="excited" data-id="element-5422" />
          </div>
          <div data-id="element-5423">
            <h3 className="font-bold text-orange-900 text-sm mb-0.5" data-id="element-5424">
              Bijna klaar!
            </h3>
            <p className="text-xs text-orange-800" data-id="element-5425">
              Controleer de gegevens hieronder. Voeg eventueel een deadline toe
              en zet de toets klaar.
            </p>
          </div>
        </div>

        <Card className="p-5 border-slate-200 space-y-4 bg-white shadow-sm" data-id="element-5426">
          <div className="flex items-center gap-4 pb-4 border-b border-slate-100" data-id="element-5427">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta?.bgColor} ${meta?.color}`} data-id="element-5428">
              {Icon && <Icon className="w-6 h-6" data-id="element-5429" />}
            </div>
            <div data-id="element-5430">
              <div className="font-black text-lg text-slate-900" data-id="element-5431">
                {meta?.label}
              </div>
              <div className="text-sm font-medium text-slate-500" data-id="element-5432">
                Groep {selectedGroup}
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-2" data-id="element-5433">
            <div data-id="element-5434">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-5435">
                Geselecteerde onderdelen
              </div>
              <div className="flex items-start gap-2 text-sm text-slate-900" data-id="element-5436">
                <Layers className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5437" />
                <div data-id="element-5438">
                  {uniqueSkills.length > 0 ? <div className="font-medium" data-id="element-5439">{uniqueSkills.join(', ')}</div> : <div className="text-slate-500 italic" data-id="element-5440">
                      Geen onderdelen geselecteerd
                    </div>}
                  <div className="text-xs text-slate-400 mt-1" data-id="element-5441">
                    {selectedFamilyIds.length}{' '}
                    {selectedFamilyIds.length === 1 ? 'onderdeel' : 'onderdelen'}
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-5442">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-5443">
                Voor wie
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900" data-id="element-5444">
                <Users className="w-4 h-4 text-slate-400" data-id="element-5445" />
                {selectionMode === 'whole' ? 'Hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-3" data-id="element-5446">
          <label className="block text-sm font-bold text-slate-700" data-id="element-5447">
            Deadline{' '}
            <span className="text-slate-400 font-normal" data-id="element-5448">(optioneel)</span>
          </label>
          <div className="flex items-center gap-3" data-id="element-5449">
            <Calendar className="w-5 h-5 text-slate-400" data-id="element-5450" />
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="flex-1" min={new Date().toISOString().split('T')[0]} data-id="element-5451" />
          </div>
          <p className="text-xs text-slate-500" data-id="element-5452">
            Na deze datum verdwijnt de toets uit het overzicht van de leerling.
          </p>
        </div>
      </div>;
  };
  const renderCurrentStep = () => {
    if (step === 1) return renderStep1();
    if (step === 2) return renderStep2();
    if (step === 3) return renderStep3();
    if (step === 4) return renderConfirmStep();
    return null;
  };
  const isNextDisabled = () => {
    if (step === 1 && !selectedDomain) return true;
    if (step === 2 && selectedFamilyIds.length === 0) return true;
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  return <SlideOver open={open} onClose={onClose} title="Toets Klaarzetten" description={`Stap ${step} van 4`} width="lg" data-id="element-5453">
      <div className="flex flex-col h-full overflow-hidden" data-id="element-5454">
        <div className="flex-1 overflow-y-auto px-1" data-id="element-5455">{renderCurrentStep()}</div>

        <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white" data-id="element-5456">
          <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100" data-id="element-5457">
            {step === 1 ? 'Annuleren' : 'Terug'}
          </Button>

          {step < 4 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" data-id="element-5458">
              Volgende <ChevronRight className="w-4 h-4 ml-1" data-id="element-5459" />
            </Button> : <Button onClick={handleConfirm} disabled={isSubmitting} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" data-id="element-5460">
              {isSubmitting ? 'Bezig...' : 'Toets Klaarzetten'}
            </Button>}
        </div>
      </div>
    </SlideOver>;
}